import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Upload, Loader2, X, FileImage } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useMyWorkerRecord, useMyReimbursements, useSubmitReimbursement } from "@/hooks/useWorkerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";

const CATEGORIES = [
  { value: "mileage", label: "Mileage" },
  { value: "meals", label: "Meals" },
  { value: "lodging", label: "Lodging" },
  { value: "tools", label: "Tools" },
  { value: "supplies", label: "Supplies" },
  { value: "parking", label: "Parking" },
  { value: "phone", label: "Phone" },
  { value: "other", label: "Other" },
];

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const statusBadge = (status: string) => {
  switch (status) {
    case "approved": return <Badge className="text-[10px] bg-primary/15 text-primary">✅ Approved</Badge>;
    case "rejected": return <Badge variant="destructive" className="text-[10px]">❌ Rejected</Badge>;
    case "paid": return <Badge className="text-[10px] bg-primary/15 text-primary">💰 Paid</Badge>;
    default: return <Badge variant="secondary" className="text-[10px]">⏳ Pending</Badge>;
  }
};

const WorkerReimbursements = () => {
  const { data: worker, isLoading: wl } = useMyWorkerRecord();
  const { data: reimbursements = [], isLoading: rl } = useMyReimbursements(worker?.id);
  const submitMutation = useSubmitReimbursement();
  const isMobile = useIsMobile();

  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    if (!description.trim() || !amount || !worker) {
      toast.error("Description and amount are required");
      return;
    }
    setUploading(true);
    try {
      let receipt_path: string | null = null;

      if (receiptFile) {
        const ext = receiptFile.name.split(".").pop() ?? "jpg";
        const path = `workers/${worker.id}/receipts/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("worker-documents")
          .upload(path, receiptFile, { upsert: false, contentType: receiptFile.type });
        if (upErr) throw upErr;
        receipt_path = path;
      }

      await submitMutation.mutateAsync({
        company_id: worker.company_id,
        worker_id: worker.id,
        personnel_project_id: worker.personnel_project_id,
        description: description.trim(),
        category,
        amount: parseFloat(amount),
        notes: notes.trim() || null,
        receipt_path,
        status: "pending",
        submitted_at: new Date().toISOString(),
      });
      toast.success("Reimbursement submitted");
      setDescription("");
      setAmount("");
      setNotes("");
      setCategory("other");
      setReceiptFile(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to submit");
    } finally {
      setUploading(false);
    }
  };

  if (wl || rl) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;

  const isPending = submitMutation.isPending || uploading;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Reimbursements</h1>

      <Card>
        <CardHeader><CardTitle className="text-base">Submit Reimbursement</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Description *</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Parking - downtown garage" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Amount *</Label><Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$0.00" /></div>
          </div>

          {/* Receipt upload */}
          <div>
            <Label>Receipt <span className="text-muted-foreground text-xs">(optional)</span></Label>
            {receiptFile ? (
              <div className="flex items-center gap-2 mt-1 p-2 border rounded-md bg-muted/30">
                <FileImage className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm truncate flex-1">{receiptFile.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(receiptFile.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => { setReceiptFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="mt-1 w-full border border-dashed rounded-md p-4 text-center text-muted-foreground hover:border-primary/50 hover:text-primary/70 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-6 w-6 mx-auto mb-1" />
                <p className="text-sm">Click to upload receipt</p>
                <p className="text-xs mt-0.5">JPG, PNG, PDF (max 10 MB)</p>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > 10 * 1024 * 1024) {
                  toast.error("File must be under 10 MB");
                  return;
                }
                setReceiptFile(f);
              }}
            />
          </div>

          <div><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional details..." rows={2} /></div>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {uploading ? "Uploading receipt…" : "Submit Reimbursement →"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">History</CardTitle></CardHeader>
        <CardContent>
          {reimbursements.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No reimbursements submitted yet</p>
          ) : isMobile ? (
            <div className="space-y-3">
              {reimbursements.map((r: any) => (
                <div key={r.id} className="border rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {r.submitted_at ? format(new Date(r.submitted_at), "MMM d, yyyy") : "—"}
                    </span>
                    {statusBadge(r.status || "pending")}
                  </div>
                  <p className="text-sm font-medium">{r.description}</p>
                  <p className="text-lg font-semibold">{fmt(r.amount)}</p>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reimbursements.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.submitted_at ? format(new Date(r.submitted_at), "MMM d") : "—"}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(r.amount)}</TableCell>
                    <TableCell>{statusBadge(r.status || "pending")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkerReimbursements;
